import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from 'recharts';

interface Fund {
  id: string;
  name: string;
  asset_class: string;
  strategy: string;
  description: string;
  aum_min: number;
  aum_max: number;
  aum_exact: number;
  inception_date: string;
  headquarters: string;
  regional_focus: string;
  vehicle_type: string;
  management_fee: number;
  performance_fee: number;
  minimum_subscription: number;
  redemption_period: string;
  lockup_months: number;
  open_to_sma: boolean;
  status: string;
  firm: {
    id: string;
    name: string;
    description: string;
    headquarters: string;
    type: string;
    inception_date: string;
    aum_min: number;
    aum_max: number;
    website: string;
    owner_id: string;
  };
}

interface Performance {
  year: number;
  month: number;
  return_pct: number;
}

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund: { color: '#0D6E8A', bg: '#EFF8FB' },
  pe: { color: '#92640A', bg: '#FDF6E7' },
  vc: { color: '#1D4ED8', bg: '#EFF4FF' },
  re: { color: '#B91C1C', bg: '#FEF2F2' },
  credit: { color: '#0F766E', bg: '#F0FDFA' },
  infrastructure: { color: '#6D28D9', bg: '#F5F3FF' },
};

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '10px 14px', fontSize: '12px' }}>
      <div style={{ color: '#6B7280', marginBottom: '4px' }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.color, fontWeight: 500 }}>{p.name}: {p.value.toFixed(2)}</div>
      ))}
    </div>
  );
};

function maskAUM(min: number, max: number): string {
  if (!min) return 'Undisclosed';
  const avg = (min + max) / 2;
  if (avg < 100) return 'Under $100mm';
  if (avg < 500) return '$100mm – $500mm';
  if (avg < 1000) return '$500mm – $1bn';
  if (avg < 5000) return '$1bn – $5bn';
  return '$5bn+';
}

function toTitleCase(str: string): string {
  if (!str) return '-';
  return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export default function FundProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fund, setFund] = useState<Fund | null>(null);
  const [performance, setPerformance] = useState<Performance[]>([]);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionChecked, setConnectionChecked] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [viewAsInvestor, setViewAsInvestor] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const { user } = useAuth();
  const { profile, firm } = useProfile();

  const isManager = profile?.role === 'manager';
  const isOwnFund = fund?.firm?.id === firm?.id;

  useEffect(() => {
    async function fetchFund() {
      const { data } = await supabase.from('funds').select(`*, firm:firms(*)`).eq('id', id).single();
      if (data) setFund(data);
      const { data: perf } = await supabase.from('fund_performance').select('*').eq('fund_id', id).order('year', { ascending: true }).order('month', { ascending: true });
      if (perf) setPerformance(perf);
      setLoading(false);
    }
    fetchFund();
  }, [id]);

  useEffect(() => {
    async function checkConnection() {
      if (!firm || !fund || isManager) { setConnectionChecked(true); return; }
      const { data } = await supabase
        .from('connections')
        .select('id, status')
        .eq('allocator_firm_id', firm.id)
        .eq('manager_firm_id', fund.firm.id)
        .maybeSingle();
      if (data) {
        setIsConnected(data.status === 'approved');
        if (data.status === 'pending') setRequested(true);
      }
      setConnectionChecked(true);
    }
    if (fund && firm) checkConnection();
  }, [fund, firm, isManager]);

  const handleRequestAccess = async () => {
    if (!user) { alert('Please log in to request access'); return; }
    if (!firm) { alert('Please complete your firm profile first'); return; }
    if (!fund) return;
    if (requesting || requested || isConnected) return;

    setRequesting(true);

    // Check if connection already exists
    const { data: existing } = await supabase
      .from('connections')
      .select('id, status')
      .eq('allocator_firm_id', firm.id)
      .eq('manager_firm_id', fund.firm.id)
      .maybeSingle();

    if (existing) {
      setRequested(true);
      setRequesting(false);
      return;
    }

    const { error } = await supabase.from('connections').insert({
      allocator_firm_id: firm.id,
      manager_firm_id: fund.firm.id,
      fund_id: null,
      requested_by: user.id,
      status: 'pending',
    });

    if (error) { alert('Error: ' + error.message); setRequesting(false); return; }
    setRequested(true);

    try {
      const { data: ownerProfile } = await supabase.from('profiles').select('full_name, email').eq('id', fund.firm.owner_id).single();
      if (ownerProfile) {
        await supabase.functions.invoke('send-email-notification', {
          body: {
            type: 'fund_access_request',
            data: {
              manager_email: ownerProfile.email,
              manager_name: ownerProfile.full_name || 'there',
              allocator_firm_name: firm.name,
              allocator_name: profile?.full_name || 'An allocator',
              fund_name: `all funds at ${fund.firm.name}`,
            },
          },
        });
      }
    } catch (err) { console.error('Email notification failed:', err); }

    setRequesting(false);
  };

  if (loading) return <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ color: '#6B7280' }}>Loading fund profile...</div></div>;
  if (!fund) return <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}><div style={{ color: '#C0222A' }}>Fund not found.</div></div>;

  const cls = classColors[fund.asset_class] || { color: '#6B7280', bg: '#F5F6F8' };

  // effectivelyConnected: manager on own fund = true, manager viewing as investor = false, allocator connected = true
  const effectivelyConnected = isManager && isOwnFund && !viewAsInvestor ? true : isConnected;

  const aumDisplay = effectivelyConnected
    ? (fund.aum_exact ? `$${fund.aum_exact}mm` : fund.aum_min ? `$${fund.aum_min}–${fund.aum_max}mm` : '-')
    : maskAUM(fund.aum_min, fund.aum_max);

  const years = [...new Set(performance.map(p => p.year))];
  const ytdByYear: Record<number, number> = {};
  years.forEach(year => {
    const yearPerf = performance.filter(p => p.year === year);
    ytdByYear[year] = yearPerf.reduce((acc, p) => acc * (1 + p.return_pct / 100), 1) - 1;
  });

  const allReturns = performance.map(p => p.return_pct);
  const annualizedReturn = allReturns.length > 0 ? (allReturns.reduce((a, b) => a + b, 0) / allReturns.length * 12).toFixed(2) : null;
  const bestMonth = allReturns.length > 0 ? Math.max(...allReturns).toFixed(2) : null;
  const worstMonth = allReturns.length > 0 ? Math.min(...allReturns).toFixed(2) : null;
  const avgMonthly = allReturns.length > 0 ? (allReturns.reduce((a, b) => a + b, 0) / allReturns.length).toFixed(2) : null;

  const annualisedVolatility = (() => {
    if (allReturns.length < 2) return null;
    const mean = allReturns.reduce((a, b) => a + b, 0) / allReturns.length;
    const variance = allReturns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / (allReturns.length - 1);
    return (Math.sqrt(variance) * Math.sqrt(12)).toFixed(2);
  })();

  const sharpeRatio = (() => {
    if (!annualizedReturn || !annualisedVolatility || parseFloat(annualisedVolatility) === 0) return null;
    return ((parseFloat(annualizedReturn) - 5) / parseFloat(annualisedVolatility)).toFixed(2);
  })();

  const maxDrawdown = (() => {
    if (allReturns.length < 2) return null;
    let peak = 100, nav = 100, maxDD = 0;
    for (const r of allReturns) {
      nav = nav * (1 + r / 100);
      if (nav > peak) peak = nav;
      const dd = (nav - peak) / peak * 100;
      if (dd < maxDD) maxDD = dd;
    }
    return maxDD.toFixed(2);
  })();

  const sortinoRatio = (() => {
    if (allReturns.length < 2 || !annualizedReturn) return null;
    const downsideReturns = allReturns.filter(r => r < 0);
    if (downsideReturns.length === 0) return null;
    const downsideVol = Math.sqrt(downsideReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) / downsideReturns.length) * Math.sqrt(12);
    if (downsideVol === 0) return null;
    return ((parseFloat(annualizedReturn) - 5) / downsideVol).toFixed(2);
  })();

  const chartData: { label: string; nav: number }[] = [];
  let navVal = 100;
  performance.forEach(p => {
    navVal = navVal * (1 + p.return_pct / 100);
    chartData.push({ label: `${monthNames[p.month - 1]} ${p.year}`, nav: parseFloat(navVal.toFixed(2)) });
  });

  const cardStyle = { background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const labelStyle = { fontSize: '11px', color: '#6B7280', marginBottom: '3px' };
  const valueStyle = { fontSize: '13.5px', fontWeight: 500, color: '#0A0A0A' };

  const requestButton = (
    <button
      onClick={handleRequestAccess}
      disabled={requesting || requested || isConnected}
      style={{
        padding: '9px 20px', borderRadius: '3px', fontSize: '13px', fontWeight: 500,
        cursor: (requesting || requested || isConnected) ? 'default' : 'pointer',
        border: `1px solid ${(requested || isConnected) ? '#E8EBF0' : '#C9A84C'}`,
        background: (requested || isConnected) ? '#F5F6F8' : '#FDF6E7',
        color: (requested || isConnected) ? '#6B7280' : '#C9A84C',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
      {isConnected ? '✓ Connected' : requested ? '✓ Request sent' : requesting ? 'Requesting...' : 'Request Access'}
    </button>
  );

  return (
    <div style={{ background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #D0D5DD', padding: '20px 28px' }}>
        <button onClick={() => navigate(-1)} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '3px', color: '#6B7280', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', marginBottom: '16px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>← Back</button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div
              onClick={() => navigate(`/alt-connect/firm/${fund.firm?.id}`)}
              style={{ fontSize: '12px', color: '#C9A84C', marginBottom: '4px', cursor: 'pointer' }}
            >
              {fund.firm?.name}
            </div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '28px', color: '#0A0A0A', marginBottom: '8px' }}>{fund.name}</h1>
            <span style={{ fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px', background: cls.bg, color: cls.color, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              {classLabels[fund.asset_class] || fund.asset_class}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {isManager && isOwnFund && (
              <button
                onClick={() => setViewAsInvestor(v => !v)}
                style={{
                  padding: '8px 16px', borderRadius: '3px', fontSize: '12px', fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                  border: `1px solid ${viewAsInvestor ? '#0D6E8A' : '#D0D5DD'}`,
                  background: viewAsInvestor ? '#EFF8FB' : '#FFFFFF',
                  color: viewAsInvestor ? '#0D6E8A' : '#6B7280',
                }}
              >{viewAsInvestor ? '👁 Investor view on' : '👁 View as investor'}</button>
            )}
            {!isManager && connectionChecked && requestButton}
          </div>
        </div>

        {viewAsInvestor && (
          <div style={{ marginTop: '12px', background: '#EFF8FB', border: '1px solid #0D6E8A', borderRadius: '4px', padding: '8px 14px', fontSize: '12px', color: '#0D6E8A' }}>
            👁 You're previewing this page as an unconnected investor would see it
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '20px', padding: '24px 28px' }}>
        <div>
          <div style={{ display: 'flex', gap: '2px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '4px', width: 'fit-content', marginBottom: '20px' }}>
            {['overview', 'performance', 'terms'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '6px 16px', borderRadius: '3px', fontSize: '13px', cursor: 'pointer', border: 'none', background: activeTab === tab ? '#F5F6F8' : 'transparent', color: activeTab === tab ? '#0A0A0A' : '#6B7280', fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: activeTab === tab ? 500 : 400, textTransform: 'capitalize' }}>{tab}</button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <>
              <div style={{ ...cardStyle }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>Fund overview</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
                  {[
                    { label: 'Asset class', value: classLabels[fund.asset_class] || fund.asset_class },
                    { label: 'Inception', value: fund.inception_date ? new Date(fund.inception_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-' },
                    {
                      label: 'AUM ($mm)', value: (
                        <span>
                          {aumDisplay}
                          {!effectivelyConnected && fund.aum_min && <span style={{ fontSize: '10px', color: '#6B7280', marginLeft: '6px' }}>🔒 Connect to see exact</span>}
                        </span>
                      )
                    },
                    { label: 'Location', value: fund.headquarters || '-' },
                  ].map((item, i) => (
                    <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                  ))}
                </div>
                <div style={{ height: '1px', background: '#E8EBF0', margin: '16px 0' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                  {[
                    { label: 'Strategy', value: fund.strategy || '-' },
                    { label: 'Regional focus', value: fund.regional_focus || '-' },
                    { label: 'Vehicle type', value: fund.vehicle_type || '-' },
                    { label: 'Open to SMAs', value: fund.open_to_sma ? 'Yes' : 'No' },
                  ].map((item, i) => (
                    <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                  ))}
                </div>
              </div>

              {fund.description && (
                <div style={cardStyle}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '12px' }}>Strategy description</div>
                  <p style={{ fontSize: '13.5px', color: '#3D4552', lineHeight: 1.7 }}>{fund.description}</p>
                </div>
              )}

              {fund.firm && (
                <div style={cardStyle}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>About {fund.firm.name}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '12px' }}>
                    {[
                      { label: 'Headquarters', value: fund.firm.headquarters || '-' },
                      { label: 'Firm AUM ($mm)', value: fund.firm.aum_min ? `$${fund.firm.aum_min}–${fund.firm.aum_max}mm` : '-' },
                      { label: 'Type', value: toTitleCase(fund.firm.type) },
                    ].map((item, i) => (
                      <div key={i}><div style={labelStyle}>{item.label}</div><div style={valueStyle}>{item.value}</div></div>
                    ))}
                  </div>
                  {fund.firm.description && <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6, marginTop: '12px' }}>{fund.firm.description}</p>}
                </div>
              )}
            </>
          )}

          {activeTab === 'performance' && (
            <>
              {!effectivelyConnected ? (
                <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔒</div>
                  <div style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '20px', color: '#0A0A0A', marginBottom: '8px' }}>Performance data is private</div>
                  <div style={{ fontSize: '13px', color: '#6B7280', maxWidth: '320px', margin: '0 auto', lineHeight: 1.6, marginBottom: '24px' }}>
                    Connect with {fund.firm?.name} to view monthly returns, performance stats, and the full NAV chart.
                  </div>
                  {!isManager && requestButton}
                </div>
              ) : performance.length === 0 ? (
                <div style={{ ...cardStyle, textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>No performance data available yet</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>The manager has not uploaded monthly returns</div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    {[
                      { label: 'Annualised return', value: annualizedReturn ? `${annualizedReturn}%` : '-' },
                      { label: 'Annualised volatility', value: annualisedVolatility ? `${annualisedVolatility}%` : '-' },
                      { label: 'Sharpe ratio', value: sharpeRatio ?? '-' },
                      { label: 'Sortino ratio', value: sortinoRatio ?? '-' },
                    ].map((stat, i) => (
                      <div key={i} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={labelStyle}>{stat.label}</div>
                        <div style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '22px', color: '#0A0A0A', marginTop: '4px' }}>{stat.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                    {[
                      { label: 'Best month', value: bestMonth ? `+${bestMonth}%` : '-' },
                      { label: 'Worst month', value: worstMonth ? `${worstMonth}%` : '-' },
                      { label: 'Avg monthly', value: avgMonthly ? `${avgMonthly}%` : '-' },
                      { label: 'Max drawdown', value: maxDrawdown ? `${maxDrawdown}%` : '-' },
                    ].map((stat, i) => (
                      <div key={i} style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={labelStyle}>{stat.label}</div>
                        <div style={{ fontFamily: 'Georgia, serif', fontWeight: 400, fontSize: '22px', color: '#0A0A0A', marginTop: '4px' }}>{stat.value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ ...cardStyle }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A' }}>Cumulative performance</div>
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>Indexed to 100 at inception</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '20px', height: '2px', background: '#0A1628', display: 'inline-block', borderRadius: '2px' }} />
                        <span style={{ fontSize: '11px', color: '#6B7280' }}>{fund.name}</span>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8EBF0" />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={false} interval={Math.floor(chartData.length / 6)} />
                        <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v.toFixed(0)}`} width={36} domain={['auto', 'auto']} />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine y={100} stroke="#D0D5DD" strokeDasharray="4 4" />
                        <Line type="linear" dataKey="nav" name="NAV" stroke="#0A1628" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#0A1628' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={cardStyle}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '16px' }}>Monthly returns (%)</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <td style={{ color: '#6B7280', padding: '6px 8px', borderBottom: '1px solid #E8EBF0' }}>Year</td>
                          {monthNames.map(m => (
                            <td key={m} style={{ color: '#6B7280', padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #E8EBF0' }}>{m}</td>
                          ))}
                          <td style={{ color: '#6B7280', padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #E8EBF0', fontWeight: 500 }}>YTD</td>
                        </tr>
                      </thead>
                      <tbody>
                        {years.map(year => (
                          <tr key={year}>
                            <td style={{ color: '#0A0A0A', padding: '8px 8px', fontWeight: 500, borderBottom: '1px solid #E8EBF0' }}>{year}</td>
                            {Array.from({ length: 12 }, (_, i) => {
                              const perf = performance.find(p => p.year === year && p.month === i + 1);
                              return (
                                <td key={i} style={{ padding: '8px 8px', textAlign: 'right', borderBottom: '1px solid #E8EBF0', color: perf ? (perf.return_pct >= 0 ? '#0A7C42' : '#C0222A') : '#6B7280' }}>
                                  {perf ? `${perf.return_pct > 0 ? '+' : ''}${perf.return_pct.toFixed(2)}%` : '-'}
                                </td>
                              );
                            })}
                            <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 500, borderBottom: '1px solid #E8EBF0', color: ytdByYear[year] >= 0 ? '#0A7C42' : '#C0222A' }}>
                              {`${(ytdByYear[year] * 100) >= 0 ? '+' : ''}${(ytdByYear[year] * 100).toFixed(2)}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'terms' && (
            <div style={cardStyle}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#0A0A0A', marginBottom: '20px' }}>Fund terms</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {[
                  { label: 'Management fee', value: fund.management_fee ? `${fund.management_fee}%` : '-' },
                  { label: 'Performance fee', value: fund.performance_fee ? `${fund.performance_fee}%` : '-' },
                  { label: 'Minimum subscription', value: fund.minimum_subscription ? `$${fund.minimum_subscription}mm` : '-' },
                  { label: 'Redemption period', value: fund.redemption_period || '-' },
                  { label: 'Lockup period', value: fund.lockup_months ? `${fund.lockup_months} months` : 'None' },
                  { label: 'Open to SMAs', value: fund.open_to_sma ? 'Yes' : 'No' },
                  { label: 'Vehicle type', value: fund.vehicle_type || '-' },
                  { label: 'Status', value: fund.status || '-' },
                ].map((item, i) => (
                  <div key={i} style={{ borderBottom: '1px solid #E8EBF0', paddingBottom: '14px' }}>
                    <div style={labelStyle}>{item.label}</div>
                    <div style={valueStyle}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div style={cardStyle}>
            <div style={{ fontSize: '12px', fontWeight: 500, color: '#0A0A0A', marginBottom: '14px' }}>Quick stats</div>
            {[
              { label: 'Strategy', value: fund.strategy || '-' },
              { label: 'Min subscription', value: fund.minimum_subscription ? `$${fund.minimum_subscription}mm` : '-' },
              { label: 'Mgmt fee', value: fund.management_fee ? `${fund.management_fee}%` : '-' },
              { label: 'Perf fee', value: fund.performance_fee ? `${fund.performance_fee}%` : '-' },
              { label: 'Regional focus', value: fund.regional_focus || '-' },
              { label: 'Open to SMAs', value: fund.open_to_sma ? 'Yes' : 'No' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #E8EBF0' }}>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>{item.label}</span>
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#0A0A0A' }}>{item.value}</span>
              </div>
            ))}
          </div>

          {!isManager && connectionChecked && (
            <div style={cardStyle}>
              <div style={{ fontSize: '12px', fontWeight: 500, color: '#0A0A0A', marginBottom: '14px' }}>Connect</div>
              {isConnected ? (
                <div style={{ background: '#FDF6E7', border: '1px solid #C9A84C', borderRadius: '4px', padding: '10px', textAlign: 'center', fontSize: '12px', color: '#C9A84C', marginBottom: '8px' }}>
                  ✓ Connected, full access granted
                </div>
              ) : (
                <>
                  <button
                    onClick={handleRequestAccess}
                    disabled={requesting || requested}
                    style={{ width: '100%', padding: '10px', borderRadius: '3px', fontSize: '13px', fontWeight: 500, cursor: (requesting || requested) ? 'default' : 'pointer', border: `1px solid ${requested ? '#E8EBF0' : '#C9A84C'}`, background: requested ? '#F5F6F8' : '#FDF6E7', color: requested ? '#6B7280' : '#C9A84C', fontFamily: 'system-ui, -apple-system, sans-serif', marginBottom: '8px' }}>
                    {requested ? '✓ Request sent' : requesting ? 'Requesting...' : 'Request Access'}
                  </button>
                  <p style={{ fontSize: '11px', color: '#6B7280', textAlign: 'center', lineHeight: 1.5 }}>
                    Your profile will be shared with the manager for review
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
